import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HeroSlider from '../components/HeroSlider';
import GoogleMap from '../components/GoogleMap';
import Counter from '../components/Counter';
import '../styles/Home.css';
import '../styles/Contact.css';

const Home: React.FC = () => {
  const { t } = useTranslation();
  // Refs for scroll animations
  const aboutImageRef = useRef<HTMLDivElement>(null);
  const galleryImageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const servicesRefs = useRef<(HTMLDivElement | null)[]>([]);
  const factsRefs = useRef<(HTMLDivElement | null)[]>([]);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  
  // Initialize refs arrays
  useEffect(() => {
    galleryImageRefs.current = galleryImageRefs.current.slice(0, 3);
    servicesRefs.current = servicesRefs.current.slice(0, 3);
    factsRefs.current = factsRefs.current.slice(0, 4);
    sectionRefs.current = sectionRefs.current.slice(0, 4);
  }, []);

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('visible');
              observer.unobserve(entry.target); // Animate only once
            }, 100);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px',
      }
    );

    const animatedElements = document.querySelectorAll(
      '.fade-in, .slide-in-left, .slide-in-right, .scale-in, .rotate-in, .bounce-in, .stagger-children'
    );
    
    animatedElements.forEach((el) => observer.observe(el));

    // New observer for gallery image zoom effect
    const galleryImgObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('gallery-img-zoomed');
          } else {
            entry.target.classList.remove('gallery-img-zoomed');
          }
        });
      },
      {
        threshold: 0.5,
      }
    );

    const galleryImgs = document.querySelectorAll('.gallery-img');
    galleryImgs.forEach((img) => galleryImgObserver.observe(img));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
      galleryImgs.forEach((img) => galleryImgObserver.unobserve(img));
    };
  }, []);

  return (
    <div className="home-wrapper">
      {/* Content Container */}
      <div className="home-content">
        {/* Hero Section - Full Screen */}
        <section id="home" className="hero-section">
          <HeroSlider />
          <div className="hero-gradient" />
        </section>

        {/* Welcome Section - Full Screen */}
        <section 
          id="about" 
          className="about-section fade-in"
          ref={(el: HTMLDivElement | null) => sectionRefs.current[0] = el}
        >
          <div className="about-container">
            <div className="about-grid">
              <div className="about-text slide-in-left">
                <h2 className="about-title">{t('home.welcome.title')}</h2>
                <p className="about-subtitle">{t('home.welcome.subtitle')}</p>
                <div className="about-features stagger-children">
                  <div className="about-feature-row">
                    <div className="about-feature-icon-wrapper">
                      <svg className="about-feature-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="about-feature-text">
                      <h3 className="about-feature-title">{t('home.welcome.features.craftsmanship.title')}</h3>
                      <p className="about-feature-desc">{t('home.welcome.features.craftsmanship.description')}</p>
                    </div>
                  </div>
                  <div className="about-feature-row">
                    <div className="about-feature-icon-wrapper">
                      <svg className="about-feature-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="about-feature-text">
                      <h3 className="about-feature-title">{t('home.welcome.features.customDesign.title')}</h3>
                      <p className="about-feature-desc">{t('home.welcome.features.customDesign.description')}</p>
                    </div>
                  </div>
                  <div className="about-feature-row">
                    <div className="about-feature-icon-wrapper">
                      <svg className="about-feature-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="about-feature-text">
                      <h3 className="about-feature-title">{t('home.welcome.features.materials.title')}</h3>
                      <p className="about-feature-desc">{t('home.welcome.features.materials.description')}</p>
                    </div>
                  </div>
                </div>
                <div className="about-link">
                  <Link to="/about" className="about-learn-more">{t('home.welcome.learnMore')}</Link>
                </div>
              </div>
              <div 
                ref={aboutImageRef}
                className="about-image slide-in-right"
              >
                <div className="about-image-wrapper">
                  <img
                    src={require('../assets/Images/WhatsApp Image 2025-04-27 at 11.19.32.jpeg')}
                    alt="BETA Jewelry Workshop"
                    className="about-img"
                  />
                </div>
                <div className="about-years scale-in">
                  <div className="about-years-number">{t('home.welcome.years.number')}</div>
                  <div className="about-years-label">{t('home.welcome.years.label')}</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Gold SVG Divider: About -> Gallery */}
        <div className="divider divider-about-gallery" />

        {/* Gallery Preview Section - Full Screen */}
        <section 
          id="gallery" 
          className="gallery-section fade-in"
          ref={(el: HTMLDivElement | null) => sectionRefs.current[1] = el}
        >
          <div className="gallery-container">
            <div className="gallery-header slide-in-left">
              <h2 className="gallery-title">{t('home.gallery.title')}</h2>
              <p className="gallery-subtitle">{t('home.gallery.subtitle')}</p>
            </div>
            <div className="gallery-grid stagger-children">
              <div ref={el => galleryImageRefs.current[0] = el} className="gallery-card scale-in">
                <img src={require('../assets/Images/WhatsApp Image 2025-04-27 at 11.19.32.jpeg')} alt={t('home.gallery.items.weddingRing.title')} className="gallery-img" />
                <div className="gallery-overlay">
                  <div className="gallery-overlay-content">
                    <h3 className="gallery-overlay-title">{t('home.gallery.items.weddingRing.title')}</h3>
                    <p className="gallery-overlay-desc">{t('home.gallery.items.weddingRing.description')}</p>
                  </div>
                </div>
              </div>

              <div ref={el => galleryImageRefs.current[1] = el} className="gallery-card scale-in">
                <img src={require('../assets/Images/WhatsApp Image 2025-04-27 at 11.16.41.jpeg')} alt={t('home.gallery.items.restoration.title')} className="gallery-img" />
                <div className="gallery-overlay">
                  <div className="gallery-overlay-content">
                    <h3 className="gallery-overlay-title">{t('home.gallery.items.restoration.title')}</h3>
                    <p className="gallery-overlay-desc">{t('home.gallery.items.restoration.description')}</p>
                  </div>
                </div>
              </div>

              <div ref={el => galleryImageRefs.current[2] = el} className="gallery-card scale-in">
                <img src={require('../assets/Images/WhatsApp Image 2025-04-27 at 11.19.32 (1).jpeg')} alt={t('home.gallery.items.moldDesign.title')} className="gallery-img" />
                <div className="gallery-overlay">
                  <div className="gallery-overlay-content">
                    <h3 className="gallery-overlay-title">{t('home.gallery.items.moldDesign.title')}</h3>
                    <p className="gallery-overlay-desc">{t('home.gallery.items.moldDesign.description')}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="gallery-link slide-in-right">
              <p className="gallery-description">{t('home.gallery.description')}</p>
              <Link to="/gallery" className="gallery-view-all">{t('home.gallery.viewAll')}</Link>
            </div>
          </div>
        </section>
        {/* Gold SVG Divider: Gallery -> Services */}
        <div className="divider divider-gallery-services" />

        {/* Services Section - With dramatic entrance */}
        <section 
          id="services" 
          className="services-section fade-in"
        >
          <div className="services-container">
            <div className="services-header slide-in-left">
              <h2 className="services-title">{t('home.services.title')}</h2>
              <p className="services-subtitle">{t('home.services.subtitle')}</p>
            </div>
            <div className="services-grid stagger-children">
              <div ref={el => servicesRefs.current[0] = el} className="services-card rotate-in">
                <div className="services-card-inner">
                  <div className="services-icon">
                    <svg className="services-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                  </div>
                  <div className="services-image">
                    <img src={require('../assets/Images/WhatsApp Image 2025-04-27 at 11.19.32.jpeg')} alt={t('home.services.customDesign.title')} className="services-img" />
                  </div>
                  <div className="services-content">
                    <h3 className="services-card-title">{t('home.services.customDesign.title')}</h3>
                    <p className="services-card-desc">{t('home.services.customDesign.description')}</p>
                    <ul className="services-features">
                      {(t('home.services.customDesign.features', { returnObjects: true }) as string[]).map((feature: string, index: number) => (
                        <li key={index} className="services-feature">{feature}</li>
                      ))}
                    </ul>
                    <Link to="/services/custom-design" className="services-link">{t('home.services.customDesign.learnMore')}</Link>
                  </div>
                </div>
              </div>
              
              {/* Service 2 */}
              <div 
                ref={el => servicesRefs.current[1] = el}
                className="services-card rotate-in"
              >
                <div className="services-card-inner">
                  <div className="services-icon">
                    <svg className="services-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <div className="services-image">
                    <img src={require('../assets/Images/WhatsApp Image 2025-04-27 at 11.16.41.jpeg')} alt={t('home.services.restoration.title')} className="services-img" />
                  </div>
                  <div className="services-content">
                    <h3 className="services-card-title">{t('home.services.restoration.title')}</h3>
                    <p className="services-card-desc">{t('home.services.restoration.description')}</p>
                    <ul className="services-features">
                      {(t('home.services.restoration.features', { returnObjects: true }) as string[]).map((feature: string, index: number) => (
                        <li key={index} className="services-feature">{feature}</li>
                      ))}
                    </ul>
                    <Link to="/services/restoration" className="services-link">{t('home.services.restoration.learnMore')}</Link>
                  </div>
                </div>
              </div>
              
              {/* Service 3 */}
              <div 
                ref={el => servicesRefs.current[2] = el}
                className="services-card rotate-in"
              >
                <div className="services-card-inner">
                  <div className="services-icon">
                    <svg className="services-icon-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                  </div>
                  <div className="services-image">
                    <img src={require('../assets/Images/WhatsApp Image 2025-04-27 at 11.19.32 (1).jpeg')} alt={t('home.services.moldDesign.title')} className="services-img" />
                  </div>
                  <div className="services-content">
                    <h3 className="services-card-title">{t('home.services.moldDesign.title')}</h3>
                    <p className="services-card-desc">{t('home.services.moldDesign.description')}</p>
                    <ul className="services-features">
                      {(t('home.services.moldDesign.features', { returnObjects: true }) as string[]).map((feature: string, index: number) => (
                        <li key={index} className="services-feature">{feature}</li>
                      ))}
                    </ul>
                    <Link to="/services/mold-design" className="services-link">{t('home.services.moldDesign.learnMore')}</Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Gold SVG Divider: Services -> Achievements */}
        <div className="divider divider-services-achievements" />

        {/* Achievements Section - With dramatic entrance */}
        <section 
          id="achievements" 
          className="achievements-section fade-in"
        >
          <div className="achievements-container">
            <div className="achievements-header slide-in-left">
              <h2 className="achievements-title">{t('home.achievements.title')}</h2>
              <p className="achievements-subtitle">{t('home.achievements.subtitle')}</p>
            </div>
            <div className="achievements-list stagger-children">
              <div className="achievements-card scale-in">
                <div className="achievements-number"><Counter end={1500} duration={2} suffix="+" /></div>
                <p className="achievements-desc">{t('home.achievements.piecesCreated')}</p>
              </div>
              <div className="achievements-card scale-in">
                <div className="achievements-number"><Counter end={98} duration={2} suffix="%" /></div>
                <p className="achievements-desc">{t('home.achievements.clientSatisfaction')}</p>
              </div>
              <div className="achievements-card scale-in">
                <div className="achievements-number"><Counter end={15} duration={2} suffix="+" /></div>
                <p className="achievements-desc">{t('home.achievements.yearsExperience')}</p>
              </div>
              <div className="achievements-card scale-in">
                <div className="achievements-number"><Counter end={25} duration={2} suffix="+" /></div>
                <p className="achievements-desc">{t('home.achievements.expertArtisans')}</p>
              </div>
            </div>
          </div>
        </section>
        {/* Gold SVG Divider: Achievements -> Contact */}
        <div className="divider divider-achievements-contact" />

        {/* Contact Section - Semantic Class Names Only */}
        <section id="contact" className="contact-section fade-in">
          <div className="contact-container">
            <div className="contact-header slide-in-left">
              <h2 className="contact-title">{t('home.contact.title')}</h2>
              <p className="contact-subtitle">{t('home.contact.subtitle')}</p>
            </div>
            <div className="contact-grid stagger-children">
              {/* Contact Information */}
              <div className="contact-info-card slide-in-left">
                <h3 className="contact-info-title">{t('home.contact.info.title')}</h3>
                <div className="contact-info-list">
                  <div className="contact-info-item">
                    <span className="contact-info-label"><span className="contact-info-icon">📍</span>{t('home.contact.info.address')}</span>
                    <span className="contact-info-value">
                      Beta Kalıp Dizayn Tasarım<br />
                      Çobançeşme, Mithatpaşa Cd.<br />
                      34196 Bahçelievler/İstanbul<br />
                      <a 
                        href="https://maps.app.goo.gl/FrtzRENH5pQsahxz6"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contact-info-link"
                      >
                        {t('home.contact.info.viewMap')}
                      </a>
                    </span>
                  </div>
                  <div className="contact-info-item">
                    <span className="contact-info-label"><span className="contact-info-icon">📞</span>{t('home.contact.info.phone')}</span>
                    <span className="contact-info-value">
                      <a href="tel:+905373053426" className="contact-info-link" dir="ltr">+90 537 305 3426</a>
                    </span>
                  </div>
                  <div className="contact-info-item">
                    <span className="contact-info-label"><span className="contact-info-icon">✉️</span>{t('home.contact.info.email')}</span>
                    <span className="contact-info-value">
                      <a href="mailto:info@betajewelry.com" className="contact-info-link">info@betajewelry.com</a>
                    </span>
                  </div>
                  <div className="contact-info-item">
                    <span className="contact-info-label"><span className="contact-info-icon">🕒</span>{t('home.contact.info.workingHours')}</span>
                    <span className="contact-info-value">
                      {t('home.contact.info.hours.weekdays')}<br />
                      {t('home.contact.info.hours.saturday')}<br />
                      {t('home.contact.info.hours.sunday')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="contact-form-card slide-in-right">
                <h3 className="contact-form-title">{t('home.contact.form.title')}</h3>
                <form className="contact-form">
                  <div className="contact-form-group">
                    <label htmlFor="name" className="contact-form-label">{t('home.contact.form.name')}</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="contact-form-input"
                    />
                  </div>
                  <div className="contact-form-group">
                    <label htmlFor="email" className="contact-form-label">{t('home.contact.form.email')}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="contact-form-input"
                    />
                  </div>
                  <div className="contact-form-group">
                    <label htmlFor="message" className="contact-form-label">{t('home.contact.form.message')}</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      className="contact-form-input"
                    />
                  </div>
                  <div className="contact-form-group">
                    <button
                      type="submit"
                      className="contact-form-button"
                    >
                      {t('home.contact.form.send')}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Google Map */}
            <div className="contact-map scale-in">
              <GoogleMap address="Beta Kalıp Dizayn Tasarım, Çobançeşme, Mithatpaşa Cd., 34196 Bahçelievler/İstanbul" />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Home; 